import { useState } from "react";
import { toast } from "sonner";
import { Upload, FileDown } from "lucide-react";
import { api } from "../../../lib/api";
import HasilImporAktivasi from "./HasilImporAktivasi";

const MIME_XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Impor tanggal aktivasi per program (brief 22 Sep A6, hal. 4–5). Alurnya
// pratinjau → konfirmasi timpa per baris → simpan: tanggal tercatat yang
// BERBEDA tidak pernah ditimpa tanpa staf melihat baris itu lebih dulu (dulu
// satu centang "timpa semua" menimpa tanpa daftar). `cbBase` & `unduhBerkas`
// dioper dari CashbackProgram.jsx (satu sumber prefix API & pengunduh blob)
// — mengimpornya dari sana akan membuat impor melingkar.
export default function PanelImporAktivasi({ program, cbBase, unduhBerkas, onSelesai }) {
  const [berkas, setBerkas] = useState(null);
  const [pratinjau, setPratinjau] = useState(null);
  const [hasil, setHasil] = useState(null);
  const [pilihTimpa, setPilihTimpa] = useState(() => new Set());
  const [busy, setBusy] = useState(false);

  // Template PER PROGRAM: IMEI, SN, tipe produk unit yang sudah terjual —
  // staf cukup mengisi kolom tanggal aktivasi.
  const unduhTemplate = () => unduhBerkas(`${cbBase}/programs/${program.id}/template-aktivasi`,
    `template-aktivasi-${program.code}.xlsx`, MIME_XLSX)
    .catch(() => toast.error("Gagal mengunduh template"));

  const kirim = async (file, mode, timpa) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post(`${cbBase}/programs/${program.id}/import-aktivasi`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
      params: { mode, timpa: JSON.stringify(timpa) },
    });
    return r.data;
  };

  const pilihBerkas = async (file) => {
    if (!file) return;
    setBusy(true); setHasil(null); setPratinjau(null);
    try {
      const d = await kirim(file, "pratinjau", []);
      setBerkas(file); setPratinjau(d); setPilihTimpa(new Set());
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membaca berkas impor"); }
    finally { setBusy(false); }
  };

  const batal = () => { setBerkas(null); setPratinjau(null); setPilihTimpa(new Set()); };

  const simpan = async () => {
    if (!berkas) return;
    setBusy(true);
    try {
      const d = await kirim(berkas, "simpan", [...pilihTimpa]);
      setHasil(d); batal();
      if (d.jumlah_gagal > 0) toast.warning(d.message); else toast.success(d.message);
      onSelesai?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan tanggal aktivasi"); }
    finally { setBusy(false); }
  };

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-2" data-testid="cb-import-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5"><Upload className="w-3.5 h-3.5" /> Impor Tanggal Aktivasi (Excel)</p>
        <div className="flex items-center gap-2">
          <button onClick={unduhTemplate} data-testid="cb-import-template" className="h-8 px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-700 hover:bg-white inline-flex items-center gap-1.5">
            <FileDown className="w-3.5 h-3.5" /> Unduh Template
          </button>
          <label className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 cursor-pointer">
            <Upload className="w-3.5 h-3.5" /> {busy ? "Memproses..." : "Impor .xlsx"}
            <input type="file" accept=".xlsx" className="hidden" data-testid="cb-import-file-input" disabled={busy}
                   onChange={(e) => { pilihBerkas(e.target.files?.[0]); e.target.value = ""; }} />
          </label>
        </div>
      </div>
      {pratinjau && (
        <div data-testid="cb-import-pratinjau" className="text-xs bg-white border border-amber-200 rounded-lg p-2.5 space-y-2">
          <HasilImporAktivasi hasil={pratinjau} pilihTimpa={pilihTimpa} onUbahTimpa={(ids) => setPilihTimpa(new Set(ids))} />
          <div className="flex items-center justify-end gap-2 pt-1">
            <button onClick={batal} disabled={busy} data-testid="cb-import-batal"
                    className="h-8 px-3 rounded-lg border border-slate-200 text-xs text-slate-700 hover:bg-slate-50">Batal</button>
            <button onClick={simpan} disabled={busy} data-testid="cb-import-simpan"
                    className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50">
              {busy ? "Menyimpan..." : `Simpan${pilihTimpa.size ? ` (timpa ${pilihTimpa.size})` : ""}`}
            </button>
          </div>
        </div>
      )}
      {hasil && (
        <div data-testid="cb-import-result" className="text-xs bg-white border border-slate-200 rounded-lg p-2.5 space-y-1.5">
          <HasilImporAktivasi hasil={hasil} />
          <button onClick={() => setHasil(null)} className="text-slate-400 hover:text-slate-700 text-[11px]">Tutup laporan</button>
        </div>
      )}
    </div>
  );
}
